import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { http } from '@/libs/http';
import type {
    Application,
    Tenant,
    User,
    Group,
    ClientSecretListItem,
    ClientSecretCreated,
    ApiKeyListItem,
    ApiKeyCreated,
    ApplicationHealth,
    ServicePrincipal,
} from '@/types';
import {
    Check,
    Plus,
    Trash2,
    ShieldOff,
    Settings,
    Globe,
    Key,
    Lock,
    ArrowLeft,
    Activity,
    Copy,
    Eye,
    EyeOff,
    Bot,
    Wifi,
    WifiOff,
    AlertTriangle,
    Image as ImageIcon,
    Users2,
    Shield,
    Smartphone,
    Monitor,
    Tablet,
} from 'lucide-react';
import { useApplication } from '@/store/application';
import { useTenants } from '@/store/tenant';
import { useUsers } from '@/store/user';
import { useGroups } from '@/store/group';
import { useServicePrincipals } from '@/store/service-principal';

import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { TabBar } from '@/components/ui/TabBar';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';

// ─── Health badge ─────────────────────────────────────────────────────────────

const HealthBadge = ({ health }: { health: ApplicationHealth | null }) => {
    if (!health) return null;
    const configs: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
        healthy: {
            color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
            icon: <Wifi className="w-3.5 h-3.5" />,
            label: 'Healthy',
        },
        degraded: {
            color: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
            icon: <AlertTriangle className="w-3.5 h-3.5" />,
            label: 'Degraded',
        },
        offline: {
            color: 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400',
            icon: <WifiOff className="w-3.5 h-3.5" />,
            label: 'Offline',
        },
        unknown: {
            color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
            icon: <Activity className="w-3.5 h-3.5" />,
            label: 'Unknown',
        },
    };
    const cfg = configs[health.status] || configs.unknown;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
            {cfg.icon}
            {cfg.label}
        </span>
    );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const ManageApplicationPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();
    const applicationStore = useApplication(id);
    const allUsers = useUsers((state) => state.users);
    const listAllUsers = useUsers((state) => state.list);
    const allGroups = useGroups((state) => state.groups);
    const listAllGroups = useGroups((state) => state.list);
    const allTenants = useTenants((state) => state.tenants);
    const listAllTenants = useTenants((state) => state.list);
    const spnStore = useServicePrincipals();

    if (!id) return null;

    const tabs = [
        { id: 'details', label: 'Details', icon: Settings },
        { id: 'tenants', label: 'Tenants', icon: Globe },
        { id: 'api-keys', label: 'API Keys', icon: Key },
        { id: 'users', label: 'Users & Groups', icon: Users2 },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'secrets', label: 'Secrets', icon: Key },
        { id: 'service-principals', label: 'Service Principals', icon: Bot },
    ];
    const [activeTab, setActiveTab] = useState<string>('details');

    const [app, setApp] = useState<Application | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [health, setHealth] = useState<ApplicationHealth | null>(null);
    const [enabledTenants, setEnabledTenants] = useState<Tenant[]>([]);
    const [appUsers, setAppUsers] = useState<User[]>([]);
    const [appGroups, setAppGroups] = useState<Group[]>([]);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [roles, setRoles] = useState<string[]>([]);
    const [secrets, setSecrets] = useState<ClientSecretListItem[]>([]);
    const [spns, setSpns] = useState<ServicePrincipal[]>([]);
    const [apiKeysByTenant, setApiKeysByTenant] = useState<Record<string, ApiKeyListItem[]>>({});

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState<
        'users' | 'groups' | 'permissions' | 'roles' | 'assignTenant' | 'newSecret' | 'newSpn' | 'newApiKey' | null
    >(null);
    const [modalValue, setModalValue] = useState('');
    const [modalValue2, setModalValue2] = useState(''); // for second field
    const [isSavingModal, setIsSavingModal] = useState(false);

    // Secret reveal state
    const [justCreatedSecret, setJustCreatedSecret] = useState<ClientSecretCreated | null>(null);
    const [secretRevealed, setSecretRevealed] = useState(false);
    const [justCreatedApiKey, setJustCreatedApiKey] = useState<(ApiKeyCreated & { tenant_name?: string }) | null>(null);
    const [apiKeyRevealed, setApiKeyRevealed] = useState(false);
    const [selectedApiKeyTenantId, setSelectedApiKeyTenantId] = useState('');

    // Delete state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        setBreadcrumbs([
            { name: 'Applications', to: '/m/applications' },
            { name: 'Manage' },
        ]);
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchData = async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            const data = await applicationStore.get();
            if (data) {
                console.log(data)
                setApp(data);
                const [tenants, users, groups, perms, appRoles, healthData, secretsList, spnsList] =
                    await Promise.all([
                        applicationStore.tenants.list().catch(() => []),
                        applicationStore.users.list().catch(() => []),
                        applicationStore.groups.list().catch(() => []),
                        applicationStore.permissions.list().catch(() => []),
                        applicationStore.roles.list().catch(() => []),
                        applicationStore.health.get().catch(() => null),
                        applicationStore.secrets.list().catch(() => []),
                        spnStore.list(id).catch(() => []),
                    ]);
                setEnabledTenants(tenants);
                setAppUsers(users);
                setAppGroups(groups);
                setPermissions(perms);
                setRoles(appRoles);
                setHealth(healthData);
                setSecrets(secretsList);
                setSpns(spnsList);
            }
        } catch {
            toast.error('Failed to load application');
            navigate('/m/applications');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (app) setApp({ ...app, logo_url: reader.result as string });
            };
            reader.readAsDataURL(file);
        }
    };

    const submitDetails = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!app) return;
        setIsSubmitting(true);
        try {
            await applicationStore.update(app);
            toast.success('Successfully updated');
        } catch {
            toast.error('Failed to update');
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDeletion = async () => {
        if (confirmText !== app?.name) return;
        setIsDeleting(true);
        try {
            await http.delete(`/mgmt/v1/applications/${app.id}`);
            toast.success('Successfully deleted');
            navigate('/m/applications');
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete');
            setIsDeleting(false);
        }
    };

    const removeTenant = async (tenantId: string) => {
        try {
            await applicationStore.tenants.unassign(tenantId);
            setEnabledTenants((prev) => prev.filter((t) => t.id !== tenantId));
            setApiKeysByTenant((prev) => {
                const next = { ...prev };
                delete next[tenantId];
                return next;
            });
            toast.success('Tenant removed');
        } catch {
            toast.error('Failed to remove tenant');
        }
    };

    const assignTenant = async (tenantId: string) => {
        try {
            await applicationStore.tenants.assign(tenantId);
            const tenant = allTenants?.find((t) => t.id === tenantId);
            if (tenant) setEnabledTenants((prev) => [...prev, tenant]);
            if (tenant) setApiKeysByTenant((prev) => ({ ...prev, [tenant.id]: prev[tenant.id] || [] }));
            toast.success('Tenant assigned');
        } catch {
            toast.error('Failed to assign tenant');
        }
    };

    const removeUser = async (userId: string) => {
        try {
            await applicationStore.users.delete(userId);
            setAppUsers((prev) => prev.filter((u) => u.id !== userId));
            toast.success('User removed');
        } catch {
            toast.error('Failed to remove user');
        }
    };

    const removeGroup = async (groupId: string) => {
        try {
            await applicationStore.groups.delete(groupId);
            setAppGroups((prev) => prev.filter((g) => g.id !== groupId));
            toast.success('Group removed');
        } catch {
            toast.error('Failed to remove group');
        }
    };

    const removePermission = async (perm: string) => {
        try {
            await applicationStore.permissions.delete(perm);
            setPermissions((prev) => prev.filter((p) => p !== perm));
            toast.success('Permission removed');
        } catch {
            toast.error('Failed to remove permission');
        }
    };

    const removeRole = async (roleId: string) => {
        try {
            await applicationStore.roles.delete(roleId);
            setRoles((prev) => prev.filter((r) => r !== roleId));
            toast.success('Role removed');
        } catch {
            toast.error('Failed to remove role');
        }
    };

    const addUser = async (userId: string) => {
        await applicationStore.users.create(userId);
        const user = allUsers?.find((u) => u.id === userId);
        if (user) setAppUsers((prev) => [...prev, user]);
    };

    const addGroup = async (groupId: string) => {
        await applicationStore.groups.create(groupId);
        const group = allGroups?.find((g) => g.id === groupId);
        if (group) setAppGroups((prev) => [...prev, group]);
    };

    const addPermission = async (perm: string) => {
        await applicationStore.permissions.create(perm);
        setPermissions((prev) => [...prev, perm]);
    };

    const addRole = async (roleId: string) => {
        await applicationStore.roles.create(roleId);
        setRoles((prev) => [...prev, roleId]);
    };

    const generateSecret = async (name: string) => {
        const created = await applicationStore.secrets.create({ name });
        setJustCreatedSecret(created);
        setSecretRevealed(false);
        setSecrets((prev) => [
            {
                id: created.id,
                application_id: created.application_id,
                name: created.name,
                hint: created.hint,
                expires_at: created.expires_at,
                is_active: true,
                created_at: created.created_at,
            },
            ...prev,
        ]);
    };

    const loadApiKeys = useCallback(async (tenants: Tenant[]) => {
        if (!tenants.length) {
            setApiKeysByTenant({});
            return;
        }
        const entries = await Promise.all(
            tenants.map(async (tenant) => {
                try {
                    const keys = await applicationStore.apiKeys.list(tenant.id);
                    return [tenant.id, keys] as const;
                } catch {
                    return [tenant.id, []] as const;
                }
            }),
        );
        setApiKeysByTenant(Object.fromEntries(entries));
    }, [applicationStore.apiKeys]);

    const generateApiKey = async (tenant: Tenant, name: string) => {
        const created = await applicationStore.apiKeys.create(tenant.id, { name });
        setJustCreatedApiKey({ ...created, tenant_name: tenant.name });
        setApiKeyRevealed(false);
        setApiKeysByTenant((prev) => ({
            ...prev,
            [tenant.id]: [created, ...(prev[tenant.id] || [])],
        }));
    };

    const revokeApiKey = async (tenant: Tenant, keyId: string) => {
        try {
            await applicationStore.apiKeys.revoke(tenant.id, keyId);
            setApiKeysByTenant((prev) => ({
                ...prev,
                [tenant.id]: (prev[tenant.id] || []).filter((item) => item.id !== keyId),
            }));
            toast.success('API key revoked');
        } catch {
            toast.error('Failed to revoke API key');
        }
    };

    const revokeSecret = async (secretId: string) => {
        try {
            await applicationStore.secrets.revoke(secretId);
            setSecrets((prev) => prev.filter((s) => s.id !== secretId));
            toast.success('Secret revoked');
        } catch {
            toast.error('Failed to revoke secret');
        }
    };

    const createSpn = async (name: string, description: string) => {
        const created = await spnStore.create({ name, description, application_id: id });
        setSpns((prev) => [...prev, created]);
    };

    const deleteSpn = async (spnId: string) => {
        try {
            await spnStore.delete(spnId);
            setSpns((prev) => prev.filter((s) => s.id !== spnId));
            toast.success('Service principal deleted');
        } catch {
            toast.error('Failed to delete service principal');
        }
    };

    const copyToClipboard = (text: string, label = 'Copied') => {
        navigator.clipboard.writeText(text).then(() => toast.success(`${label} to clipboard`));
    };

    useEffect(() => {
        if (modalOpen) {
            if (modalType === 'users') listAllUsers();
            if (modalType === 'groups') listAllGroups();
            if (modalType === 'assignTenant') listAllTenants();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen, modalType]);

    useEffect(() => {
        void loadApiKeys(enabledTenants);
    }, [enabledTenants, loadApiKeys]);

    useEffect(() => {
        if (!enabledTenants.length) {
            setSelectedApiKeyTenantId('');
            return;
        }
        const stillExists = enabledTenants.some((tenant) => tenant.id === selectedApiKeyTenantId);
        if (!stillExists) {
            setSelectedApiKeyTenantId(enabledTenants[0].id);
        }
    }, [enabledTenants, selectedApiKeyTenantId]);

    if (isLoading) return <div className="h-full flex items-center justify-center"><Loader /></div>;
    if (!app) return null;

    const nonAppUsers = allUsers?.filter((u) => !appUsers.some((au) => au.id === u.id));
    const nonAppGroups = allGroups?.filter((g) => !appGroups.some((ag) => ag.id === g.id));
    const nonAssignedTenants = allTenants?.filter((t) => !enabledTenants.some((et) => et.id === t.id));
    const selectedApiKeyTenant = enabledTenants?.find((tenant) => tenant.id === selectedApiKeyTenantId) || null;
    const selectedTenantApiKeys = selectedApiKeyTenant ? (apiKeysByTenant[selectedApiKeyTenant.id] || []) : [];

    return (
        <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title={app.name}
                subtitle={`Managing application ID: ${app.id}`}
                actions={
                    <div className="flex items-center gap-3">
                        {health && <HealthBadge health={health} />}
                        <Button variant="ghost" onClick={() => navigate('/m/applications')} className="gap-2">
                            <ArrowLeft className="w-4 h-4" />
                            Back
                        </Button>
                    </div>
                }
            />

            <div className="mt-8">
                <TabBar tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-8" />

                <div className="grid gap-6">
                    {/* ── Details ── */}
                    {activeTab === 'details' && (
                        <div className="max-w-3xl animate-in slide-in-from-bottom-4 duration-500">
                            <Card
                                title="Application Details"
                                subtitle="General information and endpoints for this service."
                                className="glass"
                            >
                                <form onSubmit={submitDetails} className="space-y-6">
                                    <div className="grid gap-6 md:grid-cols-2">
                                        {/* Row 1: Name and Base URL */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Name</label>
                                            <input
                                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                                placeholder="e.g. Frontend App"
                                                value={app.name}
                                                onChange={(e) => setApp({ ...app, name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Base URL</label>
                                            <input
                                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                                placeholder="https://app.example.com"
                                                value={app.base_url || ''}
                                                onChange={(e) => setApp({ ...app, base_url: e.target.value })}
                                            />
                                        </div>

                                        {/* Row 2: Description */}
                                        <div className="space-y-2 md:col-span-2">
                                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Description</label>
                                            <textarea
                                                rows={2}
                                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full resize-none"
                                                placeholder="Main customer facing application"
                                                value={app.description || ''}
                                                onChange={(e) => setApp({ ...app, description: e.target.value })}
                                            />
                                        </div>

                                        {/* Row 3: Documentation URL & Callback URL */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Documentation URL</label>
                                            <input
                                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                                placeholder="https://docs.example.com"
                                                value={app.docs_url || ''}
                                                onChange={(e) => setApp({ ...app, docs_url: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Callback URL</label>
                                            <input
                                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                                placeholder="https://app.example.com/callback"
                                                value={app.callback_url || ''}
                                                onChange={(e) => setApp({ ...app, callback_url: e.target.value })}
                                            />
                                        </div>

                                        {/* Row 4: Logo Upload & Toggles */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Logo</label>
                                            <div className="flex gap-4">
                                                {app.logo_url ? (
                                                    <div className="h-20 w-20 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden shrink-0">
                                                        <img src={app.logo_url} alt="Logo preview" className="w-full h-full object-cover" />
                                                    </div>
                                                ) : (
                                                    <div className="h-20 w-20 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex flex-col items-center justify-center shrink-0 text-slate-400">
                                                        <ImageIcon className="w-6 h-6 mb-1 opacity-50" />
                                                        <span className="text-[10px] text-center leading-tight">No logo</span>
                                                    </div>
                                                )}
                                                <div className="flex-1 flex flex-col justify-center gap-2">
                                                    <input
                                                        className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                                        placeholder="Logo URL..."
                                                        value={app.logo_url || ''}
                                                        onChange={(e) => setApp({ ...app, logo_url: e.target.value })}
                                                    />
                                                    <div className="flex justify-end">
                                                        <label className="cursor-pointer bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-1.5 flex items-center text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors shrink-0">
                                                            Upload File
                                                            <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-6 pt-6 pl-0 md:pl-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Is Public</label>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Allow public registration and access.</p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input type="checkbox" className="sr-only peer" checked={app.is_public || false} onChange={(e) => setApp({ ...app, is_public: e.target.checked })} />
                                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-orange-500"></div>
                                                </label>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Is Enabled</label>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Enable or disable this application immediately.</p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input type="checkbox" className="sr-only peer" checked={app.is_enabled || false} onChange={(e) => setApp({ ...app, is_enabled: e.target.checked, status: e.target.checked ? 1 : 0 })} />
                                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-orange-500"></div>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Row 5: Supported Platforms */}
                                        <div className="space-y-3 md:col-span-2 border-t border-slate-200 dark:border-slate-800 pt-5 mt-2">
                                            <div>
                                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                                                    Supported Platforms
                                                </label>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                    Select the platforms for which this application is available. Apps will be displayed as unavailable in the portal on unsupported platforms.
                                                </p>
                                            </div>
                                            <div className="flex flex-wrap gap-3 pt-1">
                                                {[
                                                    { id: 'web', label: 'Web', icon: Monitor },
                                                    { id: 'android', label: 'Android', icon: Smartphone },
                                                    { id: 'ios', label: 'iOS', icon: Tablet },
                                                ].map((platform) => {
                                                    const currentPlatforms = app.platforms || ['web', 'android', 'ios'];
                                                    const isSelected = currentPlatforms.includes(platform.id);
                                                    const Icon = platform.icon;
                                                    return (
                                                        <button
                                                            key={platform.id}
                                                            type="button"
                                                            onClick={() => {
                                                                let updated: string[];
                                                                if (isSelected) {
                                                                    updated = currentPlatforms.filter((p) => p !== platform.id);
                                                                } else {
                                                                    updated = [...currentPlatforms, platform.id];
                                                                }
                                                                setApp({ ...app, platforms: updated });
                                                            }}
                                                            className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all duration-200 ${
                                                                isSelected
                                                                    ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30 shadow-sm ring-2 ring-orange-500/20'
                                                                    : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-700'
                                                            }`}
                                                        >
                                                            <Icon className="w-4 h-4" />
                                                            <span>{platform.label}</span>
                                                            <span className={`ml-1.5 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold ${
                                                                isSelected ? 'bg-orange-500 text-white' : 'bg-slate-200 dark:bg-slate-800 text-transparent'
                                                            }`}>
                                                                ✓
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Health summary */}
                                    {health && (
                                        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 flex items-center gap-4 text-sm">
                                            <Activity className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                            <div className="flex-1">
                                                <span className="font-medium text-slate-700 dark:text-slate-300">Health: </span>
                                                <HealthBadge health={health} />
                                                {health.last_seen_at && (
                                                    <span className="ml-3 text-slate-400 text-xs">
                                                        Last seen {new Date(health.last_seen_at).toLocaleString()}
                                                    </span>
                                                )}
                                                {health.response_time_ms != null && (
                                                    <span className="ml-3 text-slate-400 text-xs">
                                                        {health.response_time_ms}ms
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                                        <Button type="submit" isLoading={isSubmitting} className="min-w-35 gap-2">
                                            <Check className="w-4 h-4" />
                                            Save Changes
                                        </Button>
                                    </div>
                                </form>
                            </Card>

                            <div className="mt-6 border border-red-200/60 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10 rounded-2xl overflow-hidden shadow-sm">
                                <div className="px-6 py-3 flex items-center gap-2 border-b border-red-100/60 dark:border-red-900/30">
                                    <AlertTriangle className="w-4.5 h-4.5 text-red-500" />
                                    <h3 className="font-bold text-red-600 dark:text-red-400 text-[15px]">
                                        Danger Zone
                                    </h3>
                                </div>
                                <div className="px-6 py-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div>
                                        <h4 className="font-bold text-slate-900 dark:text-red-100 text-[13px]">
                                            Delete this application
                                        </h4>
                                        <p className="text-xs text-slate-600 dark:text-red-300 mt-1">
                                            Once you delete an application, there is no going back. All data, assignments, and API keys will be permanently removed.
                                        </p>
                                    </div>
                                    {!showDeleteConfirm ? (
                                        <Button
                                            variant="danger"
                                            className="shrink-0 font-bold px-5 bg-red-600 hover:bg-red-700 rounded-full text-[13px]"
                                            onClick={() => setShowDeleteConfirm(true)}
                                        >
                                            Delete Application
                                        </Button>
                                    ) : (
                                        <div className="flex flex-col gap-2 shrink-0">
                                            <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                                                Type <span className="font-bold">"{app.name}"</span> to confirm
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    value={confirmText}
                                                    onChange={(e) => setConfirmText(e.target.value)}
                                                    className="px-3 py-1.5 text-sm border border-red-200 dark:border-red-800 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500/50"
                                                    placeholder={app.name}
                                                />
                                                <Button
                                                    variant="danger"
                                                    className="font-bold px-4 bg-red-600 hover:bg-red-700 rounded-md text-xs h-8 border-0"
                                                    disabled={confirmText !== app.name}
                                                    onClick={confirmDeletion}
                                                    isLoading={isDeleting}
                                                >
                                                    Confirm Delete
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    className="px-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 text-xs h-8 bg-transparent"
                                                    onClick={() => {
                                                        setShowDeleteConfirm(false);
                                                        setConfirmText("");
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Tenants ── */}
                    {activeTab === 'tenants' && (
                        <div className="animate-in slide-in-from-bottom-4 duration-500">
                            <Card
                                title="Enabled Tenants"
                                subtitle="Organizations that have access to this application."
                                noPadding
                                className="glass"
                                actions={
                                    <Button
                                        size="sm"
                                        onClick={() => { setModalType('assignTenant'); setModalValue(''); setModalOpen(true); }}
                                        className="gap-2"
                                    >
                                        <Plus className="w-3.5 h-3.5" /> Assign Tenant
                                    </Button>
                                }
                            >
                                {enabledTenants.length === 0 ? (
                                    <div className="p-12 text-center text-slate-500">
                                        No tenants are currently using this application.
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                                                    <th className="px-6 py-3">Tenant</th>
                                                    <th className="px-6 py-3">Slug</th>
                                                    <th className="px-6 py-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                                {enabledTenants.map((t) => (
                                                    <tr key={t.id} className="hover:bg-orange-50/50 dark:hover:bg-orange-500/5 transition-colors group">
                                                        <td className="px-6 py-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 flex items-center justify-center font-bold text-xs">
                                                                    {t.name?.charAt(0)}
                                                                </div>
                                                                <span className="text-sm font-bold text-slate-900 dark:text-white">{t.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            <span className="text-xs font-mono text-slate-500">{t.slug}</span>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removeTenant(t.id)}
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                                                            >
                                                                <ShieldOff className="w-4 h-4" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* ── API Keys ── */}
                    {activeTab === 'api-keys' && (
                        <div className="animate-in slide-in-from-bottom-4 duration-500">
                            {justCreatedApiKey && (
                                <div className="mb-6 p-5 rounded-2xl bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-700/40">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-500/20 text-sky-600 flex items-center justify-center flex-shrink-0">
                                            <Key className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sky-900 dark:text-sky-200 mb-1">
                                                API key created for {justCreatedApiKey.tenant_name || 'tenant'}
                                            </p>
                                            <p className="text-xs text-sky-700 dark:text-sky-400 mb-3">
                                                This key is shown only once. Copy it now and store it securely.
                                            </p>
                                            <div className="flex items-center gap-2 bg-white dark:bg-slate-900 rounded-lg px-3 py-2 border border-sky-200 dark:border-sky-700/30">
                                                <code className="flex-1 text-xs font-mono text-slate-800 dark:text-slate-200 break-all">
                                                    {apiKeyRevealed ? justCreatedApiKey.api_key : '•'.repeat(40)}
                                                </code>
                                                <button
                                                    onClick={() => setApiKeyRevealed((value) => !value)}
                                                    className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                                                >
                                                    {apiKeyRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                                <button
                                                    onClick={() => copyToClipboard(justCreatedApiKey.api_key, 'API key copied')}
                                                    className="text-slate-400 hover:text-sky-600 flex-shrink-0"
                                                >
                                                    <Copy className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <button onClick={() => setJustCreatedApiKey(null)} className="text-sky-400 hover:text-sky-600 flex-shrink-0 text-lg leading-none">×</button>
                                    </div>
                                </div>
                            )}

                            <Card
                                title="Tenant API Keys"
                                subtitle="Manage tenant-scoped API keys used for read-only management API access."
                                noPadding
                                className="glass"
                                actions={
                                    <div className="flex items-center gap-2">
                                        <select
                                            className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-all"
                                            value={selectedApiKeyTenantId}
                                            onChange={(event) => setSelectedApiKeyTenantId(event.target.value)}
                                            disabled={enabledTenants.length === 0}
                                        >
                                            {enabledTenants.length === 0 ? (
                                                <option value="">No tenant enabled</option>
                                            ) : (
                                                enabledTenants.map((tenant) => (
                                                    <option key={tenant.id} value={tenant.id}>
                                                        {tenant.name}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                        <Button
                                            size="sm"
                                            onClick={() => {
                                                if (!selectedApiKeyTenant) return;
                                                setModalType('newApiKey');
                                                setModalValue('');
                                                setModalOpen(true);
                                            }}
                                            className="gap-2"
                                            disabled={!selectedApiKeyTenant}
                                        >
                                            <Plus className="w-3.5 h-3.5" /> Generate Key
                                        </Button>
                                    </div>
                                }
                            >
                                {enabledTenants.length === 0 ? (
                                    <div className="p-12 text-center text-slate-500">
                                        Assign a tenant first to create tenant-scoped API keys.
                                    </div>
                                ) : selectedTenantApiKeys.length === 0 ? (
                                    <div className="p-12 text-center text-slate-500">
                                        No API keys for this tenant yet.
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                                                    <th className="px-6 py-3">Name</th>
                                                    <th className="px-6 py-3">Hint</th>
                                                    <th className="px-6 py-3">Created</th>
                                                    <th className="px-6 py-3">Last Used</th>
                                                    <th className="px-6 py-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                                {selectedTenantApiKeys.map((keyItem) => (
                                                    <tr key={keyItem.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                                                        <td className="px-6 py-3 text-sm font-semibold text-slate-800 dark:text-slate-200">{keyItem.name}</td>
                                                        <td className="px-6 py-3">
                                                            <code className="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                                                                {keyItem.hint}
                                                            </code>
                                                        </td>
                                                        <td className="px-6 py-3 text-sm text-slate-500">
                                                            {new Date(keyItem.created_at).toLocaleString()}
                                                        </td>
                                                        <td className="px-6 py-3 text-sm text-slate-500">
                                                            {keyItem.last_used_at ? new Date(keyItem.last_used_at).toLocaleString() : 'Never'}
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            {selectedApiKeyTenant && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => revokeApiKey(selectedApiKeyTenant, keyItem.id)}
                                                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 gap-1.5"
                                                                >
                                                                    <ShieldOff className="w-3.5 h-3.5" /> Revoke
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* ── Users & Groups ── */}
                    {activeTab === 'users' && (
                        <div className="grid gap-6 animate-in slide-in-from-bottom-4 duration-500">
                            <Card
                                title="Assigned Users"
                                subtitle="Directly authorized users for this application."
                                actions={
                                    <Button size="sm" onClick={() => { setModalType('users'); setModalValue(''); setModalOpen(true); }} className="gap-2">
                                        <Plus className="w-3.5 h-3.5" /> Assign User
                                    </Button>
                                }
                                noPadding
                                className="glass"
                            >
                                {appUsers.length === 0 ? (
                                    <div className="p-8 text-center text-slate-500 text-sm">No users assigned.</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                                {appUsers.map((u) => (
                                                    <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                                                        <td className="px-6 py-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-bold">
                                                                    {u.email?.charAt(0).toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-bold">{u.name || 'User'}</div>
                                                                    <div className="text-xs text-slate-500">{u.email}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <Button variant="ghost" size="sm" onClick={() => removeUser(u.id)} className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100">
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card>

                            <Card
                                title="Assigned Groups"
                                subtitle="Groups with collective access rights."
                                actions={
                                    <Button size="sm" onClick={() => { setModalType('groups'); setModalValue(''); setModalOpen(true); }} className="gap-2">
                                        <Plus className="w-3.5 h-3.5" /> Assign Group
                                    </Button>
                                }
                                noPadding
                                className="glass"
                            >
                                {appGroups.length === 0 ? (
                                    <div className="p-8 text-center text-slate-500 text-sm">No groups assigned.</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                                {appGroups.map((g) => (
                                                    <tr key={g.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                                                        <td className="px-6 py-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-xs font-bold">
                                                                    {g.name?.charAt(0)}
                                                                </div>
                                                                <span className="text-sm font-bold">{g.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <Button variant="ghost" size="sm" onClick={() => removeGroup(g.id)} className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100">
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* ── Security (Permissions + Roles) ── */}
                    {activeTab === 'security' && (
                        <div className="grid gap-6 animate-in slide-in-from-bottom-4 duration-500">
                            <Card
                                title="Permissions"
                                subtitle="Fine-grained access control strings for this application."
                                actions={
                                    <Button size="sm" onClick={() => { setModalType('permissions'); setModalValue(''); setModalOpen(true); }} className="gap-2">
                                        <Plus className="w-3.5 h-3.5" /> Add Permission
                                    </Button>
                                }
                                noPadding
                                className="glass"
                            >
                                {permissions.length === 0 ? (
                                    <div className="p-8 text-center text-slate-500 text-sm">No permissions defined.</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                                {permissions.map((p) => (
                                                    <tr key={p} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                                                        <td className="px-6 py-3">
                                                            <div className="flex items-center gap-2">
                                                                <Lock className="w-3.5 h-3.5 text-slate-400" />
                                                                <span className="text-sm font-mono font-bold text-slate-700 dark:text-slate-300">{p}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <Button variant="ghost" size="sm" onClick={() => removePermission(p)} className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100">
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card>

                            <Card
                                title="Roles"
                                subtitle="Predefined sets of permissions for easier management."
                                actions={
                                    <Button size="sm" onClick={() => { setModalType('roles'); setModalValue(''); setModalOpen(true); }} className="gap-2">
                                        <Plus className="w-3.5 h-3.5" /> Add Role
                                    </Button>
                                }
                                noPadding
                                className="glass"
                            >
                                {roles.length === 0 ? (
                                    <div className="p-8 text-center text-slate-500 text-sm">No roles assigned.</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                                {roles.map((r) => (
                                                    <tr key={r} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                                                        <td className="px-6 py-3">
                                                            <div className="flex items-center gap-2">
                                                                <Key className="w-3.5 h-3.5 text-slate-400" />
                                                                <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{r}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <Button variant="ghost" size="sm" onClick={() => removeRole(r)} className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100">
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* ── Client Secrets (#124) ── */}
                    {activeTab === 'secrets' && (
                        <div className="animate-in slide-in-from-bottom-4 duration-500">
                            {/* Show-once banner */}
                            {justCreatedSecret && (
                                <div className="mb-6 p-5 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/40">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 flex items-center justify-center flex-shrink-0">
                                            <Key className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-emerald-900 dark:text-emerald-200 mb-1">
                                                Secret created — copy it now!
                                            </p>
                                            <p className="text-xs text-emerald-700 dark:text-emerald-400 mb-3">
                                                This secret will <strong>never be shown again</strong>. Store it securely.
                                            </p>
                                            <div className="flex items-center gap-2 bg-white dark:bg-slate-900 rounded-lg px-3 py-2 border border-emerald-200 dark:border-emerald-700/30">
                                                <code className="flex-1 text-xs font-mono text-slate-800 dark:text-slate-200 break-all">
                                                    {secretRevealed ? justCreatedSecret.secret : '•'.repeat(32)}
                                                </code>
                                                <button
                                                    onClick={() => setSecretRevealed((v) => !v)}
                                                    className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                                                >
                                                    {secretRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                                <button
                                                    onClick={() => copyToClipboard(justCreatedSecret.secret, 'Secret copied')}
                                                    className="text-slate-400 hover:text-emerald-600 flex-shrink-0"
                                                >
                                                    <Copy className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <button onClick={() => setJustCreatedSecret(null)} className="text-emerald-400 hover:text-emerald-600 flex-shrink-0 text-lg leading-none">×</button>
                                    </div>
                                </div>
                            )}

                            <Card
                                title="Client Secrets"
                                subtitle="Secrets used by this application to authenticate with Argus IAM."
                                noPadding
                                className="glass"
                                actions={
                                    <Button
                                        size="sm"
                                        onClick={() => { setModalType('newSecret'); setModalValue(''); setModalOpen(true); }}
                                        className="gap-2"
                                    >
                                        <Plus className="w-3.5 h-3.5" /> Generate Secret
                                    </Button>
                                }
                            >
                                {secrets.length === 0 ? (
                                    <div className="p-12 text-center text-slate-500">
                                        <Key className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                                        <p className="text-sm">No client secrets. Generate one to enable API authentication.</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                                                    <th className="px-6 py-3">Name</th>
                                                    <th className="px-6 py-3">Hint</th>
                                                    <th className="px-6 py-3">Expires</th>
                                                    <th className="px-6 py-3">Last Used</th>
                                                    <th className="px-6 py-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                                {secrets.map((s) => (
                                                    <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                                                        <td className="px-6 py-3">
                                                            <div className="flex items-center gap-2">
                                                                <Key className="w-3.5 h-3.5 text-slate-400" />
                                                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{s.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            <code className="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                                                                ••••{s.hint}
                                                            </code>
                                                        </td>
                                                        <td className="px-6 py-3 text-sm text-slate-500">
                                                            {s.expires_at
                                                                ? new Date(s.expires_at).toLocaleDateString()
                                                                : <span className="text-slate-400">Never</span>}
                                                        </td>
                                                        <td className="px-6 py-3 text-sm text-slate-500">
                                                            {s.last_used_at
                                                                ? new Date(s.last_used_at).toLocaleString()
                                                                : <span className="text-slate-400">Never</span>}
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => revokeSecret(s.id)}
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 gap-1.5"
                                                            >
                                                                <ShieldOff className="w-3.5 h-3.5" />
                                                                Revoke
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* ── Service Principals (#123) ── */}
                    {activeTab === 'service-principals' && (
                        <div className="animate-in slide-in-from-bottom-4 duration-500">
                            <Card
                                title="Service Principals"
                                subtitle="Non-human identities for machine-to-machine authentication with this application."
                                noPadding
                                className="glass"
                                actions={
                                    <Button
                                        size="sm"
                                        onClick={() => { setModalType('newSpn'); setModalValue(''); setModalValue2(''); setModalOpen(true); }}
                                        className="gap-2"
                                    >
                                        <Plus className="w-3.5 h-3.5" /> Create SPN
                                    </Button>
                                }
                            >
                                {spns.length === 0 ? (
                                    <div className="p-12 text-center text-slate-500">
                                        <Bot className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                                        <p className="text-sm">No service principals linked to this application.</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                                                    <th className="px-6 py-3">Name</th>
                                                    <th className="px-6 py-3">Client ID</th>
                                                    <th className="px-6 py-3">Created</th>
                                                    <th className="px-6 py-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                                {spns.map((spn) => (
                                                    <tr key={spn.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                                                        <td className="px-6 py-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400 flex items-center justify-center">
                                                                    <Bot className="w-4 h-4" />
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-bold text-slate-800 dark:text-slate-200">{spn.name}</div>
                                                                    {spn.description && (
                                                                        <div className="text-xs text-slate-400">{spn.description}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            <div className="flex items-center gap-2">
                                                                <code className="text-xs font-mono text-slate-500">{spn.client_id}</code>
                                                                <button
                                                                    onClick={() => copyToClipboard(spn.client_id, 'Client ID copied')}
                                                                    className="text-slate-300 hover:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                >
                                                                    <Copy className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3 text-sm text-slate-500">
                                                            {spn.created_at
                                                                ? new Date(spn.created_at).toLocaleDateString()
                                                                : '—'}
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => deleteSpn(spn.id)}
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card>
                        </div>
                    )}
                </div>
            </div>

            {/* ─── Modal system ─── */}
            {modalOpen && modalType && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <Card
                        title={
                            modalType === 'users' ? 'Assign User' :
                                modalType === 'groups' ? 'Assign Group' :
                                    modalType === 'permissions' ? 'Add Permission' :
                                        modalType === 'roles' ? 'Add Role' :
                                            modalType === 'assignTenant' ? 'Assign Tenant' :
                                                modalType === 'newSecret' ? 'Generate Client Secret' :
                                                    modalType === 'newApiKey' ? 'Generate Tenant API Key' :
                                                        'Create Service Principal'
                        }
                        className="w-full max-w-md shadow-2xl border-none"
                    >
                        <div className="py-4">
                            {modalType === 'users' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-slate-500">Select a user to grant direct access to this application.</p>
                                    <select
                                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition-all"
                                        value={modalValue}
                                        onChange={(e) => setModalValue(e.target.value)}
                                    >
                                        <option value="" disabled>Select user...</option>
                                        {nonAppUsers.map((u) => (
                                            <option key={u.id} value={u.id}>{u.email || u.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {modalType === 'groups' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-slate-500">Select a group to grant collective access to this application.</p>
                                    <select
                                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition-all"
                                        value={modalValue}
                                        onChange={(e) => setModalValue(e.target.value)}
                                    >
                                        <option value="" disabled>Select group...</option>
                                        {nonAppGroups.map((g) => (
                                            <option key={g.id} value={g.id}>{g.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {modalType === 'assignTenant' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-slate-500">Select a tenant to enable this application for.</p>
                                    <select
                                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition-all"
                                        value={modalValue}
                                        onChange={(e) => setModalValue(e.target.value)}
                                    >
                                        <option value="" disabled>Select tenant...</option>
                                        {nonAssignedTenants.map((t) => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {(modalType === 'permissions' || modalType === 'roles') && (
                                <InputField
                                    label={modalType === 'permissions' ? 'Permission ID' : 'Role ID'}
                                    placeholder={modalType === 'permissions' ? 'e.g. read:reports' : 'e.g. administrator'}
                                    value={modalValue}
                                    onChange={(e) => setModalValue(e.target.value)}
                                    helpText={`Define a new ${modalType.slice(0, -1)} for this application context.`}
                                />
                            )}
                            {modalType === 'newSecret' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-slate-500">
                                        Give this secret a descriptive name (e.g. "Production CI/CD").
                                        The raw secret value will be shown <strong>only once</strong> after creation.
                                    </p>
                                    <InputField
                                        label="Secret Name"
                                        placeholder="e.g. Production Key"
                                        value={modalValue}
                                        onChange={(e) => setModalValue(e.target.value)}
                                        icon={<Key className="w-4 h-4" />}
                                    />
                                </div>
                            )}
                            {modalType === 'newSpn' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-slate-500">
                                        Create a service principal for machine-to-machine authentication.
                                        A unique <code className="font-mono text-xs">client_id</code> will be auto-generated.
                                    </p>
                                    <InputField
                                        label="Name"
                                        placeholder="e.g. Analytics Service"
                                        value={modalValue}
                                        onChange={(e) => setModalValue(e.target.value)}
                                        icon={<Bot className="w-4 h-4" />}
                                    />
                                    <InputField
                                        label="Description (optional)"
                                        placeholder="What does this SPN do?"
                                        value={modalValue2}
                                        onChange={(e) => setModalValue2(e.target.value)}
                                    />
                                </div>
                            )}
                            {modalType === 'newApiKey' && selectedApiKeyTenant && (
                                <div className="space-y-4">
                                    <p className="text-sm text-slate-500">
                                        Generate an API key for <strong>{selectedApiKeyTenant.name}</strong>. The raw key is shown once.
                                    </p>
                                    <InputField
                                        label="API Key Name"
                                        placeholder="e.g. CI pipeline"
                                        value={modalValue}
                                        onChange={(e) => setModalValue(e.target.value)}
                                        icon={<Key className="w-4 h-4" />}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
                            <Button
                                isLoading={isSavingModal}
                                disabled={!modalValue}
                                onClick={async () => {
                                    if (modalType && modalValue) {
                                        setIsSavingModal(true);
                                        try {
                                            switch (modalType) {
                                                case 'users': await addUser(modalValue); break;
                                                case 'groups': await addGroup(modalValue); break;
                                                case 'permissions': await addPermission(modalValue); break;
                                                case 'roles': await addRole(modalValue); break;
                                                case 'assignTenant': await assignTenant(modalValue); break;
                                                case 'newSecret': await generateSecret(modalValue); break;
                                                case 'newSpn': await createSpn(modalValue, modalValue2); break;
                                                case 'newApiKey':
                                                    if (selectedApiKeyTenant) await generateApiKey(selectedApiKeyTenant, modalValue);
                                                    break;
                                            }
                                            if (modalType !== 'newSecret' && modalType !== 'newApiKey') {
                                                toast.success('Successfully added');
                                            }
                                            setModalOpen(false);
                                        } catch {
                                            toast.error('Failed to add');
                                        } finally {
                                            setIsSavingModal(false);
                                        }
                                    }
                                }}
                            >
                                {modalType === 'permissions' || modalType === 'roles' ? 'Add' :
                                    modalType === 'newSecret' ? 'Generate' :
                                        modalType === 'newApiKey' ? 'Generate' :
                                            modalType === 'newSpn' ? 'Create' : 'Assign'}
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};
